import { addSeconds, differenceInMilliseconds } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import dayjs, { Dayjs } from 'dayjs';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { KeyboardShortcut, KeyboardShortcuts } from '@/shared/constants';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import {
  Tooltip as BaseTooltip,
  Stack,
  TooltipProps,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { usePlaybackSpeedState } from '@/shared/hooks/websocket/usePlaybackSpeed';
import { useTimestampState } from '@/shared/hooks/websocket/useTimestamp';

import { Tracking } from '@/shared/services/tracking';

import useCurrentGameQuery from '../../games/hooks/useCurrentGameQuery';
import { useGameTimeFormatter } from '../../games/hooks/useGameTimeFormatter';
import usePlayerHotkeys from '../hooks/usePlayerHotkeys';
import PlaybackPositionSlider from './PlaybackPositionSlider';
import PlayerCameraFeedSelectionButton from './PlayerCameraFeedSelectionButton';

import { ViewType } from '@/features/camera/types/VideoSource';
import AudioControls from './AudioControls';
import Zoom from './Zoom';
import { throttle } from 'lodash';
import { PlaybackRange } from '@/shared/types/GameStateModel';

// Internal feature flag - wire to LaunchDarkly later
const ENABLE_JUMP_ACCUMULATOR = true;
const JUMP_ACCUMULATOR_DEBOUNCE_MS = 500;

const PRIMARY_COLOR = '#ffffff';
const SECONDARY_COLOR = 'b8b8b8';
// =============================================================================
// CAMERA DISPLAY MODE BREAKPOINTS
// Based on total controls width, determines how camera names are displayed
// =============================================================================
const CAMERA_MODE_BREAKPOINT_FULL = 900; // >= 800px: show full camera names
const CAMERA_MODE_BREAKPOINT_ABBREVIATED = 600; // >= 500px: show abbreviated names
// < 500px: show dropdown

// Ethan icons
import { TenSecondsBackwardIcon } from './icons/TenSecondsBackwardIcon';
import { TenSecondsForwardIcon } from './icons/TenSecondsForwardIcon';
import { FullscreenIcon } from './icons/FullscreenIcon';
import KeyboardCmd from '@/shared/components/KeyboardCmd';

export type GamePlayBackPositions = {
  head: number;
  live: number;
  start: number;
  unavailableRanges?: PlaybackRange[];
};

type Props = {
  exitFullscreen: () => Promise<void>;
  isFullscreen: boolean;
  requestFullscreen: () => Promise<void>;
  onStatsToggle?: () => unknown;
  onMuteToggle: (isMuted: boolean) => unknown;
  onVolumeChange: (volume: number) => unknown;
  onJumpFeedback?: (accumulatedSeconds: number | null) => void;
};

type PlayerControlProps = {
  children: React.ReactNode;
  color?: string;
  disabled?: boolean;
  event?: string;
  eventMetadata?: Record<string, unknown>;
  onClick: (e: any) => void | unknown | undefined;
  title: React.ReactNode;
};

const Tooltip = (props: TooltipProps) => (
  <BaseTooltip arrow placement="top" slotProps={{ popper: { disablePortal: true } }} {...props} />
);

export const PlayerControl = ({
  children,
  color = PRIMARY_COLOR,
  disabled = false,
  event,
  eventMetadata = {},
  onClick,
  title,
}: PlayerControlProps) => {
  const handleClick = useCallback(
    (e: any) => {
      if (event) Tracking.getInstance().track(event, eventMetadata);
      onClick(e);
    },
    [event, eventMetadata, onClick],
  );

  return (
    <Box>
      <Tooltip title={title} arrow>
        <IconButton size="small" onClick={handleClick} sx={{ color }} disabled={disabled}>
          {children}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Tooltip content with styled keyboard key
export const ShortcutTooltip = ({ shortcut }: { shortcut: KeyboardShortcut }) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
    <span>{shortcut.command}</span>
    <KeyboardCmd keys={shortcut.keys} />
  </Stack>
);

// Detect 12h vs 24h once from browser locale; locale doesn't change at runtime.
// `hourCycle` is a real Intl property but missing from the bundled TS lib types.
const HOUR_CYCLE = (
  new Intl.DateTimeFormat().resolvedOptions() as { hourCycle?: string }
).hourCycle;
const IS_24_HOUR = HOUR_CYCLE === 'h23' || HOUR_CYCLE === 'h24';
const TIME_FIELD_FORMAT = IS_24_HOUR ? 'HH:mm:ss' : 'hh:mm:ss A';

const formatLocaleTime = (ms: number, timezone: string) =>
  new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(ms));

const pad2 = (n: number) => String(n).padStart(2, '0');

const PlayerControls = ({
  requestFullscreen,
  exitFullscreen,
  isFullscreen,
  onMuteToggle,
  onVolumeChange,
  onStatsToggle,
  onJumpFeedback,
}: Props) => {
  const { selectMosaic, viewType, gameStateManager } = useServerStateContext();
  const { getActiveTimezone } = useGameTimeFormatter();
  // Ref and state for responsive camera display mode
  const controlsContainerRef = useRef<HTMLDivElement>(null);
  const [controlsWidth, setControlsWidth] = useState(0);

  // Track controls container width with ResizeObserver
  useEffect(() => {
    const container = controlsContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setControlsWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Determine camera display mode based on controls width
  const cameraDisplayMode = useMemo((): 'full' | 'abbreviated' | 'dropdown' => {
    if (controlsWidth >= CAMERA_MODE_BREAKPOINT_FULL) {
      return 'full';
    } else if (controlsWidth >= CAMERA_MODE_BREAKPOINT_ABBREVIATED) {
      return 'abbreviated';
    } else {
      return 'dropdown';
    }
  }, [controlsWidth]);

  const [playbackHead, setPlaybackHead] = useState<number>(-1);
  const [playbackLive, setPlaybackLive] = useState<number>(-1);
  const [playbackStart, setPlaybackStart] = useState<number>(-1);
  const [unavailableRanges, setUnavailableRanges] = useState<PlaybackRange[]>([]);

  // Inline time editing state
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [editFieldValue, setEditFieldValue] = useState<Dayjs | null>(null);
  const [isTimeInvalid, setIsTimeInvalid] = useState(false);
  const timeInputRef = useRef<HTMLInputElement>(null);
  // Set true by Enter/Escape handlers right before they unmount the TimeField,
  // so the ghost blur fired during unmount can't double-commit or stale-commit.
  const cancelCommitRef = useRef(false);

  // Refs for jump accumulator (debounce rapid J/L presses)
  const jumpAccumulatorRef = useRef(0);
  const jumpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mirror playback bounds in refs so the debounced jump timer reads fresh
  // values (the live edge advances during the debounce window).
  const playbackHeadRef = useRef(-1);
  const playbackStartRef = useRef(-1);
  const playbackLiveRef = useRef(-1);
  useEffect(() => {
    playbackHeadRef.current = playbackHead;
    playbackStartRef.current = playbackStart;
    playbackLiveRef.current = playbackLive;
  }, [playbackHead, playbackStart, playbackLive]);

  // Validate the TimeField's Dayjs value against the available content window.
  // The field's Dayjs holds only h/m/s in browser-local tz; we recombine those
  // with the playhead's date in venue tz, then convert to UTC for bounds check.
  const validateField = useCallback(
    (val: Dayjs | null): { date: Date | null; isValid: boolean } => {
      if (!val || !val.isValid()) return { date: null, isValid: false };
      const timezone = getActiveTimezone();
      const dateInVenueTz = formatInTimeZone(new Date(playbackHead), timezone, 'yyyy-MM-dd');
      const wallClockIso = `${dateInVenueTz}T${pad2(val.hour())}:${pad2(val.minute())}:${pad2(val.second())}`;
      const newTimestamp = fromZonedTime(wallClockIso, timezone);
      const ms = newTimestamp.getTime();
      return { date: newTimestamp, isValid: ms >= playbackStart && ms <= playbackLive };
    },
    [playbackHead, playbackStart, playbackLive, getActiveTimezone],
  );

  const [, setTimestamp] = useTimestampState();

  const gameQuery = useCurrentGameQuery();

  const [, adjustPlaybackSpeed] = usePlaybackSpeedState();
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const { enableQuadView } = useFlags();

  const playbackMinute = useMemo(() => Math.round(playbackHead / (60 * 1000)), [playbackHead]);

  // Get timezone abbreviation for tooltip
  const timezoneAbbreviation = useMemo(() => {
    if (playbackHead <= 0) return '';
    const tz = getActiveTimezone();
    return formatInTimeZone(new Date(playbackHead), tz, 'zzz');
  }, [playbackHead, getActiveTimezone]);

  useEffect(() => {
    // A backgrounded tab keeps advancing the playback head, but nobody is
    // watching: those minutes are not playback progress, so don't report them
    if (document.hidden) return;

    Tracking.getInstance().track('Playback Progress', {
      category: 'Playback',
      unit: 'minutes',
      type: 'playback',
      minutes: playbackMinute,
    });
  }, [playbackMinute]);

  const setPlaybackSpeed = useCallback(
    (speed: number) => {
      if (!adjustPlaybackSpeed) return;
      adjustPlaybackSpeed(speed);
    },
    [adjustPlaybackSpeed],
  );

  // This section would be added to your PlayerControls component
  useEffect(() => {
    if (!gameStateManager) return;

    console.warn('Setting up event listeners for playback controls');

    // Get initial state immediately if available
    const currentState = gameStateManager.getCurrentState();
    if (currentState) {
      setPlaybackHead(currentState.head / 1_000_000);
      setPlaybackLive(currentState.live / 1_000_000);
      setUnavailableRanges(currentState.unavailable_ranges ?? []);
      setIsPlaying(currentState.speed === 1);
    }

    // Create throttled update functions that will only execute once per second
    const throttledHeadUpdate = throttle((head) => {
      setPlaybackHead(head / 1_000_000);
    }, 1000);

    const throttledLiveUpdate = throttle((live) => {
      setPlaybackLive(live / 1_000_000);
    }, 1000);

    // Speed changes should probably remain immediate for better UX
    const handleSpeedChange = (speed: number) => {
      setIsPlaying(speed === 1);
    };

    // Create a throttled state update function
    const throttledStateUpdate = throttle((state) => {
      setPlaybackHead(state.head / 1_000_000);
      setPlaybackLive(state.live / 1_000_000);
      setUnavailableRanges(state.unavailable_ranges ?? []);
      setIsPlaying(state.speed === 1);
    }, 1000);

    // Register for state updates to catch everything
    gameStateManager.on('stateUpdate', throttledStateUpdate);

    // Register specific event handlers
    gameStateManager.on('headChange', throttledHeadUpdate);
    gameStateManager.on('speedChange', handleSpeedChange); // Not throttled
    gameStateManager.on('liveChange', throttledLiveUpdate);

    return () => {
      // Clean up all event listeners
      gameStateManager.off('stateUpdate', throttledStateUpdate);
      gameStateManager.off('headChange', throttledHeadUpdate);
      gameStateManager.off('speedChange', handleSpeedChange);
      gameStateManager.off('liveChange', throttledLiveUpdate);

      // Cancel any pending throttled executions
      throttledHeadUpdate.cancel();
      throttledLiveUpdate.cancel();
      throttledStateUpdate.cancel();
    };
  }, [gameStateManager]);

  const playbackPositionSeconds = useMemo(
    () => Math.round(differenceInMilliseconds(playbackHead, playbackStart) / 1000),
    [playbackHead, playbackStart],
  );

  const endPositionSeconds = useMemo(
    () => Math.round(differenceInMilliseconds(playbackLive, playbackStart) / 1000),
    [playbackLive, playbackStart],
  );

  const isEditing = false;

  const handleLiveButtonClick = useCallback(() => {
    if (!setTimestamp) return;
    setTimestamp(new Date(playbackLive));
    Tracking.getInstance().track('Jump To Live', {
      category: 'Controls',
      type: 'Jump',
      source: 'Video Control',
    });
  }, [setTimestamp, playbackLive]);

  const handleJump = useCallback(
    (jumpSeconds: number) => {
      if (!setTimestamp) return;

      // Clamp the requested target to the available content window
      // [playbackStart, playbackLive]. Bounds < 0 mean "not yet known", so
      // skip clamping until the game state has loaded.
      const clampToAvailable = (timeMs: number) => {
        const start = playbackStartRef.current;
        const live = playbackLiveRef.current;
        if (start < 0 || live < 0) return timeMs;
        return Math.max(start, Math.min(live, timeMs));
      };

      // Feature flag check - use original behavior if disabled
      if (!ENABLE_JUMP_ACCUMULATOR) {
        const head = playbackHeadRef.current;
        const target = addSeconds(head, jumpSeconds).getTime();
        const clamped = clampToAvailable(target);
        if (clamped === head) return;
        Tracking.getInstance().track('Seek', {
          category: 'Controls',
          source: 'Jump Button',
        });
        setTimestamp(new Date(clamped));
        return;
      }

      // Accumulate the jump
      jumpAccumulatorRef.current += jumpSeconds;
      onJumpFeedback?.(jumpAccumulatorRef.current);

      // Clear existing timer
      if (jumpTimerRef.current) {
        clearTimeout(jumpTimerRef.current);
      }

      // Set new debounce timer
      jumpTimerRef.current = setTimeout(() => {
        const totalJump = jumpAccumulatorRef.current;
        if (totalJump !== 0) {
          const head = playbackHeadRef.current;
          const target = addSeconds(head, totalJump).getTime();
          const clamped = clampToAvailable(target);
          if (clamped !== head) {
            Tracking.getInstance().track('Seek', {
              category: 'Controls',
              source: 'Jump Button',
            });
            setTimestamp(new Date(clamped));
          }
        }

        // Reset accumulator
        jumpAccumulatorRef.current = 0;

        // Keep overlay visible briefly after jump executes, then hide
        setTimeout(() => onJumpFeedback?.(null), 500);
      }, JUMP_ACCUMULATOR_DEBOUNCE_MS);
    },
    [setTimestamp, onJumpFeedback],
  );

  const handlePlayPause = useCallback(
    (e: Event) => {
      if (!setPlaybackSpeed) return;
      setPlaybackSpeed(isPlaying ? 0 : 1);
      Tracking.getInstance().track('Playback', {
        category: 'Controls',
        type: !isPlaying ? 'Pause' : 'Play',
        source: e.type === 'click' ? 'Video Control' : 'Keyboard Shortcut',
      });
    },
    [isPlaying, setPlaybackSpeed],
  );

  // Single toggle so the F hotkey and the on-screen button share one path.
  // Tracks click vs keyboard via `e.type` (matches the handlePlayPause pattern).
  const handleFullscreenToggle = useCallback(
    (e: Event) => {
      const source = e.type === 'click' ? 'Video Control' : 'Keyboard Shortcut';
      if (isFullscreen) {
        Tracking.getInstance().track('Exit Fullscreen', { category: 'Controls', source });
        exitFullscreen();
      } else {
        Tracking.getInstance().track('Enter Fullscreen', { category: 'Controls', source });
        requestFullscreen();
      }
    },
    [isFullscreen, requestFullscreen, exitFullscreen],
  );

  const handleJumpForwardTenSeconds = useCallback(
    (e: Event) => {
      Tracking.getInstance().track('Jump forward 10 Seconds', {
        category: 'Controls',
        unit: 'Second',
        type: 'Jump',
        direction: 'Forward',
        source: e.type === 'click' ? 'Video Control' : 'Keyboard Shortcut',
      });
      handleJump(10);
    },
    [handleJump],
  );

  const handleJumpBackwardTenSeconds = useCallback(
    (e: Event) => {
      Tracking.getInstance().track('Jump backward 10 Seconds', {
        category: 'Controls',
        unit: 'Second',
        type: 'Jump',
        direction: 'Backward',
        source: e.type === 'click' ? 'Video Control' : 'Keyboard Shortcut',
      });
      handleJump(-10);
    },
    [handleJump],
  );

  // Cleanup jump accumulator timer on unmount
  useEffect(() => {
    return () => {
      if (jumpTimerRef.current) {
        clearTimeout(jumpTimerRef.current);
      }
    };
  }, []);

  const audioEnabled = useMemo(() => {
    return (gameQuery.data as any)?.enableAudio;
  }, [gameQuery?.data]);

  const inLiveMode = useMemo(() => {
    const status = (gameQuery.data as any)?.status;
    return status === 'live';
  }, [gameQuery?.data]);

  useEffect(() => {
    if (!gameQuery?.data || !gameQuery.data?.startTimestamp) return;
    setPlaybackStart(gameQuery.data.startTimestamp.getTime());
  }, [gameQuery?.data]);

  const isQuad = useMemo(() => viewType === ViewType.MOSAIC, [viewType]);

  const isLiveEdge = useMemo(
    () => playbackPositionSeconds / endPositionSeconds > 0.96,
    [playbackPositionSeconds, endPositionSeconds],
  );

  const handleSetTimestamp = useCallback(
    (value: Date | null) => {
      if (!setTimestamp || !value) return;
      if (value.getTime() < playbackStart || value.getTime() > playbackLive) {
        console.warn('Timestamp is out of bounds');
        return;
      }

      Tracking.getInstance().track('Jump To Timestamp', {
        category: 'Controls',
        type: 'Jump',
        source: 'Video Control',
      });

      setTimestamp(value);
    },
    [setTimestamp, playbackStart, playbackLive],
  );

  // Inline time editing handlers
  const handleTimeClick = useCallback(
    (e?: { type?: string }) => {
      // Don't re-seed if the user is already editing — would clobber their input.
      if (isEditingTime) return;
      Tracking.getInstance().track('Jump to Time', {
        category: 'Controls',
        type: 'Edit',
        source: e?.type === 'click' ? 'Video Control' : 'Keyboard Shortcut',
      });
      // Seed the field with the current playhead expressed as venue wall-clock.
      // Dayjs interprets the ISO string in browser-local tz; we only ever read
      // h/m/s back from it, so the tz the field "thinks" in is irrelevant.
      const wallClockIso = formatInTimeZone(
        new Date(playbackHead),
        getActiveTimezone(),
        "yyyy-MM-dd'T'HH:mm:ss",
      );
      setEditFieldValue(dayjs(wallClockIso));
      setIsEditingTime(true);
      setIsTimeInvalid(false);
      if (setPlaybackSpeed) setPlaybackSpeed(0); // Pause when editing
      setTimeout(() => timeInputRef.current?.select(), 0);
    },
    [isEditingTime, playbackHead, getActiveTimezone, setPlaybackSpeed],
  );

  usePlayerHotkeys(
    handlePlayPause,
    handleJumpForwardTenSeconds,
    handleJumpBackwardTenSeconds,
    handleTimeClick,
    handleFullscreenToggle,
  );

  const handleFieldChange = useCallback(
    (val: Dayjs | null) => {
      setEditFieldValue(val);
      const { isValid } = validateField(val);
      setIsTimeInvalid(!isValid);
    },
    [validateField],
  );

  // Wrapper-level blur. MUI X TimeField's sections each fire blur on internal
  // focus moves (←/→ arrows, tabbing between h/m/s/AM-PM), so we must check
  // that focus is actually leaving the whole wrapper before committing.
  const handleWrapperBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      // relatedTarget = where focus is going. If it's still inside us, this is
      // section navigation, not a real blur — ignore.
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      // Enter/Escape already handled this transition; drop the ghost blur.
      if (cancelCommitRef.current) {
        cancelCommitRef.current = false;
        return;
      }
      const { date, isValid } = validateField(editFieldValue);
      if (!isValid || !date) {
        setIsTimeInvalid(true);
        return;
      }
      setIsEditingTime(false);
      setIsTimeInvalid(false);
      handleSetTimestamp(date);
    },
    [editFieldValue, validateField, handleSetTimestamp],
  );

  const handleFieldKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const { date, isValid } = validateField(editFieldValue);
        if (!isValid || !date) {
          setIsTimeInvalid(true);
          return;
        }
        cancelCommitRef.current = true;
        setIsEditingTime(false);
        setIsTimeInvalid(false);
        handleSetTimestamp(date);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelCommitRef.current = true;
        setIsEditingTime(false);
        setIsTimeInvalid(false);
      }
    },
    [editFieldValue, validateField, handleSetTimestamp],
  );

  const [zoom, setZoom] = useState(1);

  const onZoomChange = useCallback(
    (newZoom: number) => {
      setZoom(newZoom);
    },
    [setZoom],
  );

  return (
    <>
      <PlaybackPositionSlider
        gamePlaybackPositions={{
          head: playbackHead,
          live: playbackLive,
          start: playbackStart,
          unavailableRanges,
        }}
        gameId={(gameQuery.data as any)?.trackingId}
      />

      <Box
        ref={controlsContainerRef}
        sx={{ overflowX: 'auto', p: 1, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
      >
        <Stack direction="row" spacing={1} px={2} sx={{ alignItems: 'center' }}>
          <PlayerControl
            title={<ShortcutTooltip shortcut={KeyboardShortcuts.ShuttleBackward} />}
            onClick={handleJumpBackwardTenSeconds}
          >
            <TenSecondsBackwardIcon />
          </PlayerControl>

          <PlayerControl
            title={<ShortcutTooltip shortcut={KeyboardShortcuts.PlayPause} />}
            onClick={handlePlayPause}
          >
            {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
          </PlayerControl>

          <PlayerControl
            title={<ShortcutTooltip shortcut={KeyboardShortcuts.ShuttleForward} />}
            onClick={handleJumpForwardTenSeconds}
          >
            <TenSecondsForwardIcon />
          </PlayerControl>

          {audioEnabled && (
            <AudioControls
              onMuteToggle={onMuteToggle}
              onVolumeChange={onVolumeChange}
              initialVolume={50}
            />
          )}
          <Stack direction="row" spacing={2}>
            {isEditingTime ? (
              <Box
                onBlur={handleWrapperBlur}
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <TimeField
                  inputRef={timeInputRef}
                  value={editFieldValue}
                  onChange={handleFieldChange}
                  onKeyDown={handleFieldKeyDown}
                  format={TIME_FIELD_FORMAT}
                  variant="standard"
                  size="small"
                  sx={(theme) => ({
                    width: IS_24_HOUR ? '90px' : '120px',
                    // Color the visible section spans and let the hidden input inherit.
                    // MUI X renders each h/m/s/AM-PM as its own span, not a single input.
                    '& .MuiPickersInputBase-root, & .MuiInputBase-root': {
                      color: isTimeInvalid ? theme.palette.error.main : 'white',
                      fontSize: 'inherit',
                      fontFamily: 'inherit',
                    },
                    '& .MuiPickersInputBase-sectionContent': {
                      fontVariantNumeric: 'tabular-nums',
                      color: 'inherit',
                    },
                    '& input': {
                      color: 'inherit',
                      textAlign: 'center',
                      padding: 0,
                    },
                    // Underline: resting, focused, and hover (hover would otherwise
                    // flash the theme's hover color and break the error signal).
                    '& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline:hover:not(.Mui-disabled):before':
                      {
                        borderBottomColor: isTimeInvalid ? theme.palette.error.main : 'white',
                      },
                  })}
                />
              </Box>
            ) : (
              <Tooltip title={<ShortcutTooltip shortcut={KeyboardShortcuts.EditTime} />}>
                <Typography
                  onClick={handleTimeClick}
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    userSelect: 'none',
                    cursor: 'pointer',
                  }}
                  noWrap
                  sx={{
                    '&:hover': {
                      opacity: 0.8,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {formatLocaleTime(playbackHead, getActiveTimezone())}
                </Typography>
              </Tooltip>
            )}
          </Stack>

          {isEditing && (
            <Zoom
              gameDuration={endPositionSeconds}
              gameStart={playbackStart}
              onZoomChange={onZoomChange}
            >
              {' '}
            </Zoom>
          )}
        </Stack>

        <Stack spacing={2} direction="row">
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            {inLiveMode && (
              <Stack
                direction="row"
                spacing={1}
                style={{ marginRight: 20, cursor: 'pointer' }}
                onClick={handleLiveButtonClick}
              >
                <CircleRoundedIcon color={isLiveEdge ? 'error' : 'disabled'} sx={{ width: 16 }} />
                <Typography>Live</Typography>
              </Stack>
            )}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {enableQuadView && (
              <>
                <Typography variant="subtitle1" noWrap>
                  Select View
                </Typography>

                <PlayerControl
                  title="Quad Camera"
                  onClick={() => selectMosaic('Video Player Toolbar')}
                  color={isQuad ? PRIMARY_COLOR : SECONDARY_COLOR}
                >
                  <GridViewRoundedIcon />
                </PlayerControl>
              </>
            )}

            <PlayerCameraFeedSelectionButton
              value={0}
              disabled={false}
              displayMode={cameraDisplayMode}
              onError={function (error: string | undefined): void {}}
            />

            <PlayerControl
              title={<ShortcutTooltip shortcut={KeyboardShortcuts.Fullscreen} />}
              onClick={handleFullscreenToggle}
              color={isFullscreen ? PRIMARY_COLOR : SECONDARY_COLOR}
            >
              {isFullscreen ? <FullscreenExitRoundedIcon /> : <FullscreenIcon />}
            </PlayerControl>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

export default PlayerControls;
